import { Badge, Box, Button, Group, Select, Stack, Table, Text, TextInput } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../../lib/api.js";
import {
  useAttributeDefs,
  useSetSystemAttribute,
  type SystemAttributeEntry,
} from "../../hooks/useMaintenance.js";

interface Props {
  customerId: string;
  systemId: string;
  attributes: SystemAttributeEntry[];
}

interface EditTarget {
  defId: string;
  name: string;
  allowedValues: string[];
  initialValue: string;
}

/**
 * Abschnitt „Merkmale" im aufgeklappten System (Aufgabe 16): zeigt gesetzte
 * Werte und erlaubt, ein Merkmal zu setzen oder zu ändern. Die Definitionen
 * (Name, erlaubte Werte) werden bewusst in den Einstellungen gepflegt
 * (`SystemAttributeDefsTab`) — hier wird nur angezeigt und ein Wert gesetzt,
 * das bleibt für jeden Angemeldeten offen (Spec E6).
 */
export function SystemAttributesSection({ customerId, systemId, attributes }: Props) {
  const { t } = useTranslation();
  const { data: defs } = useAttributeDefs();
  const [editing, setEditing] = useState<EditTarget | null>(null);

  const unsetDefs = (defs ?? []).filter((d) => !attributes.some((a) => a.defId === d.id));

  function toggleEditing(a: SystemAttributeEntry) {
    setEditing((cur) =>
      cur?.defId === a.defId
        ? null
        : { defId: a.defId, name: a.name, allowedValues: a.allowedValues, initialValue: a.value },
    );
  }

  function startAdding(defId: string) {
    const def = unsetDefs.find((d) => d.id === defId);
    if (!def) return;
    setEditing({ defId: def.id, name: def.name, allowedValues: def.allowedValues, initialValue: "" });
  }

  return (
    <Box>
      <Text fw={600} size="sm" mb={4}>
        {t("maintenance.card.attributes.title")}
      </Text>

      {attributes.length === 0 ? (
        <Text c="dimmed" size="sm">
          {t("maintenance.card.attributes.empty")}
        </Text>
      ) : (
        <Table withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("common.name")}</Table.Th>
              <Table.Th>{t("maintenance.card.attributes.value")}</Table.Th>
              <Table.Th>{t("common.actions")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {attributes.map((a) => (
              <Table.Tr key={a.defId}>
                <Table.Td>{a.name}</Table.Td>
                <Table.Td>
                  <Badge variant="light" color="gray">
                    {a.value}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Button size="compact-xs" variant="subtle" onClick={() => toggleEditing(a)}>
                    {t("common.edit")}
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {editing !== null && (
        <AttributeForm
          customerId={customerId}
          systemId={systemId}
          target={editing}
          onDone={() => setEditing(null)}
        />
      )}

      <Group mt="xs">
        {unsetDefs.length > 0 ? (
          <Select
            size="xs"
            placeholder={t("maintenance.card.attributes.addButton")}
            data={unsetDefs.map((d) => ({ value: d.id, label: d.name }))}
            value={null}
            onChange={(v) => v && startAdding(v)}
            style={{ minWidth: 220 }}
          />
        ) : (
          (defs ?? []).length === 0 && (
            <Text c="dimmed" size="xs">
              {t("maintenance.card.attributes.noDefs")}
            </Text>
          )
        )}
      </Group>
    </Box>
  );
}

function AttributeForm({
  customerId,
  systemId,
  target,
  onDone,
}: {
  customerId: string;
  systemId: string;
  target: EditTarget;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const setAttribute = useSetSystemAttribute(customerId);
  const [value, setValue] = useState(target.initialValue);

  return (
    <Box p="sm" mt="xs" style={{ border: "1px dashed var(--mantine-color-gray-4)", borderRadius: 8 }}>
      <Stack gap="xs">
        <Text size="xs" fw={500}>
          {target.name}
        </Text>
        {target.allowedValues.length > 0 ? (
          <Select
            size="xs"
            label={t("maintenance.card.attributes.value")}
            data={target.allowedValues}
            value={value.length > 0 ? value : null}
            allowDeselect={false}
            onChange={(v) => setValue(v ?? "")}
            style={{ maxWidth: 240 }}
          />
        ) : (
          <TextInput
            size="xs"
            label={t("maintenance.card.attributes.value")}
            value={value}
            onChange={(e) => setValue(e.currentTarget.value)}
            style={{ maxWidth: 240 }}
          />
        )}
        <Group gap="xs">
          <Button
            size="xs"
            disabled={value.trim().length === 0}
            loading={setAttribute.isPending}
            onClick={() =>
              setAttribute.mutate(
                { systemId, defId: target.defId, value: value.trim() },
                { onSuccess: onDone },
              )
            }
          >
            {t("common.save")}
          </Button>
          <Button size="xs" variant="subtle" onClick={onDone}>
            {t("common.cancel")}
          </Button>
        </Group>
        {setAttribute.isError && (
          <Text c="red" size="xs">
            {setAttribute.error instanceof ApiError
              ? setAttribute.error.message
              : t("maintenance.card.attributes.saveError")}
          </Text>
        )}
      </Stack>
    </Box>
  );
}
